import { useCallback, useEffect, useRef, useState } from 'react';
import InviteGroupsSetting from '../components/InviteGroupsSetting';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrg } from '../lib/org';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import { Spinner, ErrorAlert, EmptyState, UserSearchPicker } from '../components/ui';
import * as orgApi from '../api/organizations';
import * as domainsApi from '../api/domains';
import {
  RENAME_WARNING,
  ROLE_LABELS,
  earliestSchedule,
  localInputToIso,
  renameNeedsSchedule,
  toLocalInput,
} from '../lib/domains';
import * as billingApi from '../api/billing';
import BillingNotificationSettings from '../components/BillingNotificationSettings';
import AudienceSettingsForm from '../components/AudienceSettingsForm';
import OrgTypeSettings from '../components/OrgTypeSettings';
import { cn, ui } from '../lib/ui';
import PaymentsSettingsLink from '../components/PaymentsSettingsLink';
import DnsRecordsTable from '../components/DnsRecordsTable';

// docs/Decisions.md D46: pending-verification (publish the TXT record) ->
// provisioning (point the domain at the CNAME target) -> active. The two
// pre-D46 statuses only appear until the poller migrates a record.
const STATUS_PILL = {
  'pending-verification': { label: 'Waiting for verification', tone: 'info' },
  'pending-dns': { label: 'Pending DNS', tone: 'info' },
  'pending-certificate': { label: 'Pending certificate', tone: 'info' },
  provisioning: { label: 'Provisioning', tone: 'warning' },
  active: { label: 'Active', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  removing: { label: 'Removing', tone: 'warning' },
};

const MAX_DOMAINS = 5;

function DomainDnsRecords({ records }) {
  if (!records || records.length === 0) return null;
  return (
    <div className="mt-3">
      <h3 className="mt-0 text-body-s mb-2 font-semibold">DNS records to create</h3>
      <DnsRecordsTable records={records} showPurpose />
    </div>
  );
}

// Pill class per tone name (STATUS_PILL tones and member status).
const PILL = {
  success: ui.pillSuccess,
  warning: ui.pillWarning,
  danger: ui.pillDanger,
  info: ui.pillInfo,
  neutral: ui.pillNeutral,
};

/**
 * Rename a live address (docs/Decisions.md D56 item 3): the new address is
 * set up first and the old one retired once the new one is live. With
 * members, the rename must be scheduled at least an hour ahead.
 */
function RenameForm({ domain, memberCount, onDone, onCancel }) {
  const toast = useToast();
  const scheduled = renameNeedsSchedule(memberCount);
  const [toDomain, setToDomain] = useState('');
  const [when, setWhen] = useState(() => toLocalInput(earliestSchedule()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { domain: toDomain.trim().toLowerCase() };
      if (scheduled) body.scheduledAt = localInputToIso(when);
      const data = await domainsApi.renameDomain(domain.domainId, body);
      toast.success(
        data.newDomain
          ? `Setting up ${data.newDomain.domain}. Add its DNS records to finish the rename.`
          : `Rename to ${body.domain} scheduled`
      );
      onDone();
    } catch (err) {
      setError(err.message || 'The rename could not be scheduled');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="mt-3" onSubmit={submit} aria-label={`Rename ${domain.domain}`}>
      <p className={cn(ui.alertWarning, 'mb-3!')} role="note">
        {RENAME_WARNING}
        {scheduled
          ? ' Your organization has members, so schedule the rename for a quiet time, at least an hour from now.'
          : ''}
      </p>
      {error && <ErrorAlert message={error} />}
      <div className="flex flex-wrap items-end gap-3">
        <label className={cn(ui.field, 'flex-1 basis-64 mb-0!')}>
          <span className={ui.label}>New address</span>
          <input
            className={ui.input}
            value={toDomain}
            placeholder={domain.domain}
            onChange={e => setToDomain(e.target.value)}
            required
          />
        </label>
        {scheduled && (
          <label className={cn(ui.field, 'mb-0!')}>
            <span className={ui.label}>When</span>
            <input
              className={ui.input}
              type="datetime-local"
              value={when}
              min={toLocalInput(earliestSchedule())}
              onChange={e => setWhen(e.target.value)}
              required
            />
          </label>
        )}
        <button className={ui.btnPrimary} type="submit" disabled={busy || !toDomain.trim()}>
          {busy ? 'Saving...' : scheduled ? 'Schedule rename' : 'Rename now'}
        </button>
        <button className={ui.btnGhost} type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function DomainCard({
  domain,
  onVerify,
  onRemove,
  verifyingId,
  removingId,
  memberCount,
  onChanged,
}) {
  const toast = useToast();
  const [renaming, setRenaming] = useState(false);
  const pill = STATUS_PILL[domain.status] || { label: domain.status, tone: 'neutral' };
  const canVerify = ['pending-verification', 'pending-dns', 'pending-certificate', 'provisioning'].includes(
    domain.status
  );
  const canRemove = domain.status !== 'removing';
  const showDns = ['pending-verification', 'provisioning', 'pending-dns', 'pending-certificate'].includes(
    domain.status
  );
  const nextStep =
    domain.status === 'pending-verification'
      ? 'Add the TXT record below at your DNS provider, then choose Check now. We check every few minutes as well.'
      : domain.status === 'provisioning'
        ? 'Point the domain at the CNAME target below. The certificate is issued once DNS points here; the domain goes live on its own after that.'
        : null;

  return (
    <div className={cn(ui.card, 'mb-4')}>
      <div className="flex items-center justify-between flex-wrap gap-2"
      >
        <div className="flex items-center gap-3">
          <strong className="text-body-l">{domain.domain}</strong>
          <span className={ui.pillInfo}>{ROLE_LABELS[domain.role] || 'Portal'}</span>
          <span className={PILL[pill.tone] || ui.pillNeutral}>{pill.label}</span>
        </div>
        <div className="flex gap-2">
          {domain.status === 'active' && !domain.rename && !renaming && (
            <button className={ui.btnSecondary} onClick={() => setRenaming(true)} type="button">
              Rename
            </button>
          )}
          {canVerify && (
            <button
              className={ui.btnSecondary}
              disabled={verifyingId === domain.domainId}
              onClick={() => onVerify(domain.domainId)}
              type="button"
            >
              {verifyingId === domain.domainId ? 'Checking...' : 'Check now'}
            </button>
          )}
          {canRemove && (
            <button
              className={ui.btnDanger}
              disabled={removingId === domain.domainId}
              onClick={() => onRemove(domain)}
              type="button"
            >
              {removingId === domain.domainId ? 'Removing...' : 'Remove'}
            </button>
          )}
        </div>
      </div>

      {nextStep && <p className={cn(ui.hint, 'mt-2')}>{nextStep}</p>}
      {showDns && <DomainDnsRecords records={domain.dnsRecords} />}

      {domain.rename && (
        <div className={cn(ui.alertInfo, 'mt-3 mb-0!')}>
          {domain.rename.error === 'new-address-taken'
            ? `The rename to ${domain.rename.toDomain} could not start: that address is already registered.`
            : domain.rename.startedAt
              ? `Renaming to ${domain.rename.toDomain}: set up its DNS records below. This address keeps working until the new one is live.`
              : `Rename to ${domain.rename.toDomain} scheduled for ${new Date(domain.rename.scheduledAt).toLocaleString()}.`}{' '}
          <button
            type="button"
            className={cn(ui.btnGhost, ui.btnSm)}
            onClick={async () => {
              try {
                await domainsApi.cancelRename(domain.domainId);
                toast.success('Rename cancelled');
                onChanged();
              } catch (err) {
                toast.error(err.message || 'The rename could not be cancelled');
              }
            }}
          >
            Cancel rename
          </button>
        </div>
      )}
      {renaming && (
        <RenameForm
          domain={domain}
          memberCount={memberCount}
          onCancel={() => setRenaming(false)}
          onDone={() => {
            setRenaming(false);
            onChanged();
          }}
        />
      )}

      {domain.status === 'failed' && (
        <p className={cn(ui.alertError, 'mt-3 mb-0!')}>
          Domain provisioning failed. Remove this domain and set it up again.
        </p>
      )}
    </div>
  );
}

function AddDomainForm({ onAdd, busy, disabled, hasWebsite }) {
  const [domain, setDomain] = useState('');
  const [role, setRole] = useState('portal');

  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed) return;
    onAdd(trimmed, role);
    setDomain('');
  }

  return (
    <form className="flex gap-3 items-end flex-wrap"
      onSubmit={handleSubmit}
    >
      <div className={cn(ui.field, 'flex-1 basis-64 mb-0!')}>
        <label className={ui.label} htmlFor="add-domain-input">
          Custom domain
        </label>
        <input
          id="add-domain-input"
          className={ui.input}
          type="text"
          placeholder="portal.example.com"
          value={domain}
          onChange={e => setDomain(e.target.value)}
          disabled={busy || disabled}
        />
      </div>
      <label className={cn(ui.field, 'mb-0!')} htmlFor="add-domain-role">
        <span className={ui.label}>Serves</span>
        <select
          id="add-domain-role"
          className={ui.input}
          value={role}
          onChange={e => setRole(e.target.value)}
          disabled={busy || disabled}
        >
          <option value="portal">The portal (members sign in)</option>
          <option value="website" disabled={hasWebsite}>
            The website (your template site)
          </option>
        </select>
      </label>
      <button
        className={ui.btnPrimary}
        type="submit"
        disabled={busy || disabled || !domain.trim()}
      >
        {busy ? 'Adding...' : 'Add domain'}
      </button>
    </form>
  );
}

function DomainManager() {
  const toast = useToast();
  const confirm = useConfirm();
  const { activeOrg } = useOrg();

  const [domains, setDomains] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addBusy, setAddBusy] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const runIdRef = useRef(0);

  const fetchDomains = useCallback(async () => {
    const id = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await domainsApi.listDomains();
      if (id !== runIdRef.current) return;
      setDomains(data.domains || []);
    } catch (err) {
      if (id !== runIdRef.current) return;
      setError(err.message || 'Failed to load domains');
    } finally {
      if (id === runIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  async function handleAdd(domain, role) {
    setAddBusy(true);
    setError(null);
    try {
      await domainsApi.setupDomain(domain, role);
      toast.success(`Domain ${domain} added - configure the DNS records shown below`);
      await fetchDomains();
    } catch (err) {
      setError(err.message || 'Failed to add domain');
    } finally {
      setAddBusy(false);
    }
  }

  async function handleVerify(domainId) {
    setVerifyingId(domainId);
    try {
      const data = await domainsApi.verifyDomain(domainId);
      const newStatus = data.domain?.status;
      if (newStatus === 'active') {
        toast.success('Domain is now active');
      } else {
        toast.success(data.message || 'Verification initiated');
      }
      await fetchDomains();
    } catch (err) {
      toast.error(err.message || 'Verification failed');
    } finally {
      setVerifyingId(null);
    }
  }

  async function handleRemove(domain) {
    const ok = await confirm({
      title: `Remove ${domain.domain}?`,
      message:
        'This will remove the custom domain and revoke its certificate. ' +
        'Users accessing the platform through this domain will lose access.',
      danger: true,
    });
    if (!ok) return;

    setRemovingId(domain.domainId);
    try {
      await domainsApi.removeDomain(domain.domainId);
      toast.success(`Domain ${domain.domain} removal initiated`);
      await fetchDomains();
    } catch (err) {
      toast.error(err.message || 'Failed to remove domain');
    } finally {
      setRemovingId(null);
    }
  }

  if (loading && domains === null) return <Spinner />;

  const activeDomains = (domains || []).filter(
    d => d.status !== 'failed' && d.status !== 'removing'
  );
  const atLimit = activeDomains.length >= MAX_DOMAINS;

  return (
    <section>
      <h2 className={ui.h2}>Custom domains</h2>
      <p className={cn(ui.hint, 'mb-4')}>
        Your own addresses: the website shows your template site, and the portal is where members
        sign in (for example www.example.org and portal.example.org). You can have up to{' '}
        {MAX_DOMAINS} active domains, one of them the website, and rename a live address at any
        time.
      </p>

      {error && <ErrorAlert message={error} />}

      {domains && domains.length === 0 && (
        <EmptyState message="No custom domains configured. Add one below to get started." />
      )}

      {domains &&
        domains.length > 0 &&
        domains.map(d => (
          <DomainCard
            key={d.domainId}
            domain={d}
            onVerify={handleVerify}
            onRemove={handleRemove}
            verifyingId={verifyingId}
            removingId={removingId}
            memberCount={activeOrg?.memberCount ?? 0}
            onChanged={fetchDomains}
          />
        ))}

      <div className={cn(ui.card, 'mt-4')}>
        <h3 className="mt-0 mb-3 text-title-s">Add a domain</h3>
        {atLimit && (
          <p className={cn(ui.alertInfo, 'mb-3!')}>
            You have reached the maximum of {MAX_DOMAINS} active domains.
          </p>
        )}
        <AddDomainForm
          onAdd={handleAdd}
          busy={addBusy}
          disabled={atLimit}
          hasWebsite={activeDomains.some(d => d.role === 'website')}
        />
      </div>
    </section>
  );
}

const TIER_NAMES = { base: 'Base', mid: 'Mid', top: 'Top' };

const FEATURE_LABELS = {
  'video-hosting': 'Video hosting',
  events: 'Events',
  calendar: 'Calendar',
  groups: 'Groups',
  inbox: 'Inbox (messaging)',
  forums: 'Forums',
  verification: 'Verification workflows',
  'custom-roles': 'Custom roles',
  'saml-sso': 'SAML SSO',
  'api-access': 'API access',
  'ribbon-designer': 'Ribbon and badge designer (premium)',
  'badge-visibility': 'Ribbon and badge visibility scopes (premium)',
  'site-upload': 'Hosted site upload (premium)',
  'payments-advanced': 'Advanced payments (premium)',
};

function TierCard({ tier, currentTierId, onSelect, selecting }) {
  const isCurrent = tier.tierId === currentTierId;

  return (
    <div className={cn(ui.card, 'mb-4', isCurrent && 'border-2 border-dcon-brand')}>
      <div className="flex items-center justify-between flex-wrap gap-2"
      >
        <div className="flex items-center gap-3">
          <strong className="text-body-l">{tier.name}</strong>
          {isCurrent && <span className={ui.pillSuccess}>Current</span>}
        </div>
        {!isCurrent && tier.purchasable && (
          <button
            className={ui.btnPrimary}
            disabled={selecting}
            onClick={() => onSelect(tier.tierId)}
            type="button"
          >
            {selecting ? 'Redirecting...' : 'Select'}
          </button>
        )}
      </div>
      {/* docs/Decisions.md D63 item 4: never a button that can only fail. */}
      {!isCurrent && !tier.purchasable && (
        <p className={cn(ui.hint, 'mb-0')}>
          {tier.tierId === 'base'
            ? 'To move to this plan, cancel your subscription from Manage billing.'
            : 'Not available for online signup yet. Contact the platform team to move to this plan.'}
        </p>
      )}
      <div className="mt-3">
        <h4 className="mt-0 text-body-s mb-2 font-semibold">Features</h4>
        <ul className="m-0 pl-4 text-body-s">
          {tier.features.map(f => (
            <li key={f}>{FEATURE_LABELS[f] || f}</li>
          ))}
        </ul>
      </div>
      {tier.limits && (
        <div className="mt-3 text-body-s text-dcon-ink-muted">
          {tier.limits.maxMembers === -1
            ? 'Unlimited members'
            : `Up to ${tier.limits.maxMembers} members`}
          {' · '}
          {tier.limits.maxVideos === -1
            ? 'Unlimited videos'
            : `Up to ${tier.limits.maxVideos} videos`}
          {' · '}
          {tier.limits.maxStorageGb != null && `${tier.limits.maxStorageGb} GB storage`}
        </div>
      )}
    </div>
  );
}

function BillingManager() {
  const toast = useToast();

  const [usage, setUsage] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const runIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const id = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const [usageData, tiersData] = await Promise.all([billingApi.getUsage(), billingApi.listTiers()]);
      if (id !== runIdRef.current) return;
      setUsage(usageData);
      setTiers(tiersData.tiers || []);
    } catch (err) {
      if (id !== runIdRef.current) return;
      setError(err.message || 'Failed to load billing data');
    } finally {
      if (id === runIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Upgrade prompts link here with #billing (src/lib/upgrade.js); the router
  // does not scroll to a hash, so bring the plans into view once they load.
  const sectionRef = useRef(null);
  useEffect(() => {
    if (!loading && window.location.hash === '#billing') {
      sectionRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [loading]);

  async function handleSelectTier(tierId) {
    setSelecting(true);
    try {
      const data = await billingApi.createCheckout(
        tierId,
        `${window.location.origin}/org/settings?billing=success`,
        `${window.location.origin}/org/settings?billing=cancelled`
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
      setSelecting(false);
    }
  }

  async function handleOpenPortal() {
    setPortalLoading(true);
    try {
      const data = await billingApi.createPortalSession(
        `${window.location.origin}/org/settings`
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.message || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  }

  if (loading && !usage) return <Spinner />;

  const currentTier = TIER_NAMES[usage?.tier] || usage?.tier || 'Base';
  const hasSubscription = Boolean(usage?.subscription?.stripeSubscriptionId);

  return (
    <section className="mt-8 scroll-mt-header" id="billing" ref={sectionRef}>
      <h2 className={ui.h2}>Billing and plan</h2>

      {error && <ErrorAlert message={error} />}

      <div className={cn(ui.card, 'mb-4')}>
        <div className="flex items-center justify-between flex-wrap gap-2"
        >
          <div>
            <p className="m-0">
              <strong>Current plan:</strong> {currentTier}
            </p>
            {usage?.subscription?.billingEmail && (
              <p className={cn(ui.hint, 'm-0!')}>
                Billing email: {usage.subscription.billingEmail}
              </p>
            )}
            {usage?.subscription?.gracePeriodEnds && (
              <p className={cn(ui.alertWarning, 'mt-2 mb-0!')}>
                Subscription cancelled. Access continues until{' '}
                {new Date(usage.subscription.gracePeriodEnds).toLocaleDateString()}.
              </p>
            )}
          </div>
          {hasSubscription && (
            <button
              className={ui.btnSecondary}
              disabled={portalLoading}
              onClick={handleOpenPortal}
              type="button"
            >
              {portalLoading ? 'Opening...' : 'Manage billing'}
            </button>
          )}
        </div>

        {usage?.limits && (
          <div className="mt-3 text-body-s">
            <strong>Usage:</strong>{' '}
            {usage.usage?.memberCount ?? 0} /{' '}
            {usage.limits.maxMembers === -1 ? '∞' : usage.limits.maxMembers} members
          </div>
        )}

        {usage?.features && usage.features.length > 0 && (
          <div className="mt-3">
            <strong className="text-body-s">Enabled features:</strong>
            <div className="flex flex-wrap gap-2 mt-2">
              {usage.features.map(f => (
                <span key={f} className={ui.pillInfo}>
                  {FEATURE_LABELS[f] || f}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <BillingNotificationSettings />

      <h3 className={ui.h3}>Available plans</h3>
      <p className={cn(ui.hint, 'mb-4')}>
        Select a plan to change your organization&apos;s subscription.
      </p>

      {tiers.map(tier => (
        <TierCard
          key={tier.tierId}
          tier={tier}
          currentTierId={usage?.tier || 'base'}
          onSelect={handleSelectTier}
          selecting={selecting}
        />
      ))}
    </section>
  );
}

function AudienceManager() {
  const { activeOrg } = useOrg();
  const [planFeatures, setPlanFeatures] = useState(null);

  useEffect(() => {
    billingApi
      .getUsage()
      .then(data => setPlanFeatures(data.features || null))
      .catch(() => setPlanFeatures(null));
  }, [activeOrg?.orgId]);

  if (!activeOrg?.orgId) return null;
  return (
    <section className="mt-8">
      <h2 className={ui.h2}>Feature settings</h2>
      <p className={cn(ui.hint, 'mb-4')}>
        Who outside your organization can see your events, and how many videos members can play.
        Open a feature to change it. There are no user limits; event capacity is set on each event
        and forum access on each forum category.
      </p>
      <AudienceSettingsForm
        key={activeOrg.orgId}
        orgId={activeOrg.orgId}
        planFeatures={planFeatures}
        saveLabel="Save feature settings"
      />
    </section>
  );
}

const ORG_ROLES = [
  { value: 'member', label: 'Member' },
  { value: 'org-moderator', label: 'Moderator' },
  { value: 'org-admin', label: 'Admin' },
];

function MemberManager() {
  const { activeOrg } = useOrg();
  const toast = useToast();
  const confirm = useConfirm();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);

  const runIdRef = useRef(0);

  const fetchMembers = useCallback(async () => {
    const id = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await orgApi.listMembers(activeOrg.orgId);
      if (id !== runIdRef.current) return;
      setMembers(data.members || []);
    } catch (err) {
      if (id !== runIdRef.current) return;
      setError(err.message || 'Failed to load members');
    } finally {
      if (id === runIdRef.current) setLoading(false);
    }
  }, [activeOrg?.orgId]);

  useEffect(() => {
    if (activeOrg?.orgId) fetchMembers();
  }, [fetchMembers, activeOrg?.orgId]);

  async function handleAdd(user) {
    setAdding(true);
    setError(null);
    try {
      await orgApi.addMember(activeOrg.orgId, user.userId, 'member');
      toast.success(`${user.displayName || user.email} added as member`);
      await fetchMembers();
    } catch (err) {
      setError(err.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  }

  async function handleRoleChange(member, newRole) {
    setUpdatingId(member.userId);
    try {
      await orgApi.updateMemberRole(activeOrg.orgId, member.userId, newRole);
      toast.success(`${member.displayName || member.email} is now ${newRole}`);
      await fetchMembers();
    } catch (err) {
      toast.error(err.message || 'Failed to update role');
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleRemove(member) {
    const ok = await confirm({
      title: `Remove ${member.displayName || member.email}?`,
      message: 'This member will lose access to the organization and its content.',
      danger: true,
    });
    if (!ok) return;

    setRemovingId(member.userId);
    try {
      await orgApi.removeMember(activeOrg.orgId, member.userId);
      toast.success(`${member.displayName || member.email} removed`);
      await fetchMembers();
    } catch (err) {
      toast.error(err.message || 'Failed to remove member');
    } finally {
      setRemovingId(null);
    }
  }

  async function handleInvite(e) {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    setError(null);
    try {
      await orgApi.inviteMember(activeOrg.orgId, email, inviteRole);
      toast.success(`Invitation sent to ${email}`);
      setInviteEmail('');
      setInviteRole('member');
      await fetchMembers();
    } catch (err) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  }

  const memberIds = members.map(m => m.userId);

  return (
    <section className="mt-8">
      <h2 className={ui.h2}>Members</h2>
      <p className={cn(ui.hint, 'mb-4')}>
        Manage who has access to this organization and their roles.
      </p>

      {error && <ErrorAlert message={error} />}

      {loading && members.length === 0 ? (
        <Spinner />
      ) : (
        <>
          {members.length === 0 ? (
            <EmptyState message="No members yet. Add someone below." />
          ) : (
            <div className={cn(ui.tableWrap, 'mb-4')}>
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.userId}>
                      <td className="align-top">{m.status === 'invited' ? (m.inviteEmail || '-') : (m.displayName || m.userId)}</td>
                      <td className="align-top">{m.email || m.inviteEmail || '-'}</td>
                      <td className="align-top">
                        <select
                          className={cn(ui.input, 'w-auto! px-2! py-1! text-body-s')}
                          aria-label={`Role for ${m.displayName || m.inviteEmail || m.email || m.userId}`}
                          value={m.role}
                          disabled={updatingId === m.userId || m.status === 'invited'}
                          onChange={e => handleRoleChange(m, e.target.value)}
                        >
                          {ORG_ROLES.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="align-top">
                        <span className={m.status === 'active' ? ui.pillSuccess : m.status === 'invited' ? ui.pillInfo : ui.pillWarning}>
                          {m.status || 'active'}
                        </span>
                      </td>
                      <td className="align-top">
                        <button
                          className={cn(ui.btnDanger, ui.btnSm)}
                          disabled={removingId === m.userId}
                          onClick={() => handleRemove(m)}
                          type="button"
                        >
                          {removingId === m.userId ? 'Removing...' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={ui.card}>
            <h3 className="mt-0 mb-3 text-title-s">Add existing platform user</h3>
            {adding && <Spinner />}
            <UserSearchPicker onPick={handleAdd} exclude={memberIds} />
          </div>

          <div className={cn(ui.card, 'mt-4')}>
            <h3 className="mt-0 mb-3 text-title-s">Invite by email</h3>
            <p className={cn(ui.hint, 'mb-3')}>
              Send an invitation to someone who may or may not have a platform account yet.
            </p>
            <form className="flex gap-2 items-end flex-wrap" onSubmit={handleInvite}>
              <div className={cn(ui.field, 'flex-1 min-w-48 mb-0!')}>
                <label className={ui.label} htmlFor="invite-email">Email</label>
                <input
                  id="invite-email"
                  className={ui.input}
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  required
                  disabled={inviting}
                />
              </div>
              <div className={cn(ui.field, 'mb-0!')}>
                <label className={ui.label} htmlFor="invite-role">Role</label>
                <select
                  id="invite-role"
                  className={ui.input}
                  value={inviteRole}
                  onChange={e => setInviteRole(e.target.value)}
                  disabled={inviting}
                >
                  {ORG_ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <button className={ui.btnPrimary} type="submit" disabled={inviting || !inviteEmail.trim()}>
                {inviting ? 'Sending...' : 'Send invite'}
              </button>
            </form>
          </div>
        </>
      )}
    </section>
  );
}

function OrgProfile() {
  const { activeOrg, refresh } = useOrg();
  const toast = useToast();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', logoUrl: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function startEditing() {
    setForm({
      name: activeOrg.name || '',
      description: activeOrg.description || '',
      logoUrl: activeOrg.logoUrl || '',
    });
    setEditing(true);
    setError(null);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await orgApi.updateOrganization(activeOrg.orgId, {
        name: form.name.trim(),
        description: form.description.trim(),
        logoUrl: form.logoUrl.trim(),
      });
      toast.success('Organization profile updated');
      await refresh();
      setEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="m-0 text-title-m">Organization profile</h2>
        <Link to="/org/setup" className={cn(ui.btnSecondary, ui.btnSm)}>
          Run setup wizard
        </Link>
      </div>

      {error && <ErrorAlert message={error} />}

      <div className={ui.card}>
        {!editing ? (
          <>
            <div className="flex items-start gap-4">
              {activeOrg.logoUrl && (
                <img className="w-16 h-16 object-contain rounded-dcon-md"
                  src={activeOrg.logoUrl}
                  alt={`${activeOrg.name} logo`}
                />
              )}
              <div className="flex-1">
                <h3 className="mt-0 mb-1 text-title-s">{activeOrg.name}</h3>
                <p className={cn(ui.hint, 'mt-0! mb-1')}>
                  Slug: <code className="font-mono text-caption">{activeOrg.slug}</code>
                </p>
                {activeOrg.description && (
                  <p className="mt-2 mx-0 mb-0 text-body-s">
                    {activeOrg.description}
                  </p>
                )}
                <div className="mt-2 flex gap-2 flex-wrap">
                  <span className={ui.pillInfo}>{activeOrg.tier || 'base'}</span>
                  <span className={activeOrg.status === 'active' ? ui.pillSuccess : ui.pillWarning}>
                    {activeOrg.status || 'active'}
                  </span>
                  <span className={ui.pillNeutral}>{activeOrg.visibility || 'invite_only'}</span>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <button className={ui.btnSecondary} onClick={startEditing} type="button">
                Edit profile
              </button>
            </div>
          </>
        ) : (
          <div className="max-w-lg">
            <div className={ui.field}>
              <label className={ui.label} htmlFor="org-name">Name</label>
              <input
                id="org-name"
                className={ui.input}
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                maxLength={200}
              />
            </div>
            <div className={ui.field}>
              <label className={ui.label} htmlFor="org-desc">Description</label>
              <textarea
                id="org-desc"
                className={ui.input}
                rows={3}
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                maxLength={1000}
              />
            </div>
            <div className={ui.field}>
              <label className={ui.label} htmlFor="org-logo">Logo URL</label>
              <input
                id="org-logo"
                className={ui.input}
                type="url"
                value={form.logoUrl}
                onChange={e => setForm(p => ({ ...p, logoUrl: e.target.value }))}
                placeholder="https://example.com/logo.png"
              />
            </div>
            <div className="flex gap-2">
              <button className={ui.btnPrimary} onClick={handleSave} disabled={saving || !form.name.trim()} type="button">
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button className={ui.btnSecondary} onClick={() => setEditing(false)} disabled={saving} type="button">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default function OrgSettings() {
  const { activeOrg } = useOrg();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOrgAdmin = activeOrg?.role === 'org-admin';

  useEffect(() => {
    const billing = searchParams.get('billing');
    if (billing === 'success') {
      toast.success('Subscription updated successfully');
      searchParams.delete('billing');
      setSearchParams(searchParams, { replace: true });
    } else if (billing === 'cancelled') {
      toast.info('Checkout cancelled');
      searchParams.delete('billing');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  if (!activeOrg) {
    return (
      <>
        <h1 className={ui.h1}>Organization settings</h1>
        <EmptyState message="No organization selected. Join or create an organization first." />
      </>
    );
  }

  if (!isOrgAdmin) {
    return (
      <>
        <h1 className={ui.h1}>Organization settings</h1>
        <div className={ui.alertInfo}>
          Only organization administrators can manage settings. Contact your org admin for access.
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className={ui.h1}>{activeOrg.name} - Settings</h1>
      <OrgProfile />
      <OrgTypeSettings />
      <MemberManager />
      <InviteGroupsSetting />
      <BillingManager />
      <PaymentsSettingsLink />
      <AudienceManager />
      <DomainManager />
    </>
  );
}
